import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { FaLightbulb, FaUsers, FaChartLine } from "react-icons/fa";
import { Link } from "react-router-dom";
import AOS from "aos";
import "aos/dist/aos.css";
import { COLORS } from "../constants/colors";

const AboutUs = () => {
  useEffect(() => {
    AOS.init({ duration: 800 });
  }, []);

  const services = [
    {
      icon: <FaLightbulb className="text-6xl mb-4 mx-auto" style={{ color: COLORS.primaryDark }} />,
      title: "Innovative Solutions",
      description:
        "We craft creative, technology-driven strategies tailored to empower educational and business growth.",
    },
    {
      icon: <FaUsers className="text-6xl mb-4 mx-auto" style={{ color: COLORS.primaryDark }} />,
      title: "Expert Team",
      description:
        "Our experienced professionals are passionate about delivering high-quality solutions and long-term support.",
    },
    {
      icon: <FaChartLine className="text-6xl mb-4 mx-auto" style={{ color: COLORS.primaryDark }} />,
      title: "Data-Driven Approach",
      description:
        "We analyze data and feedback to continuously improve performance, efficiency, and outcomes.",
    },
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: COLORS.light }}>
      {/* 🌈 Hero Section */}
      <section
        className="relative h-[600px] flex items-center justify-start bg-cover bg-center px-6 md:px-20"
        style={{
          backgroundImage:
            "url('https://images.pexels.com/photos/1181406/pexels-photo-1181406.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1')",
        }}
      >
        <div className="absolute inset-0" style={{ background: "rgba(0,0,0,0.75)" }}></div>
        <div
          className="absolute inset-0"
          style={{ background: COLORS.gradientHero, opacity: 0.2 }}
        ></div>

        <motion.div
          className="relative z-10 max-w-4xl text-left"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
        >
          <p
            className="font-medium text-lg mb-4 tracking-wide"
            style={{ color: COLORS.secondary }}
          >
            Your Partner in Empowering Educational Innovation
          </p>
          <h1
            className="text-5xl md:text-7xl font-bold"
            style={{ color: COLORS.white }}
          >
            About Gurukul ERP
          </h1>
        </motion.div>
      </section>

      {/* 🧭 Intro Section */}
      <motion.section
        className="max-w-6xl mx-auto py-20 px-6 text-center"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <h2
          className="text-4xl font-bold mb-6"
          style={{ color: COLORS.primaryDark }}
        >
          What is Gurukul ERP?
        </h2>
        <p className="text-lg md:text-xl leading-relaxed max-w-3xl mx-auto" style={{ color: COLORS.textSecondary }}>
          Gurukul ERP is a powerful, integrated platform that simplifies school management by
          connecting administrators, teachers, students, and parents on a single intelligent system.
          From attendance to communication, Gurukul ensures every process runs seamlessly.
        </p>
      </motion.section>

      {/* 💡 Why Choose Gurukul */}
      <motion.section
        className="max-w-6xl mx-auto py-16 px-6 flex flex-col md:flex-row items-center gap-10"
        initial={{ opacity: 0, scale: 0.95 }}
        whileInView={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.8 }}
      >
        <div className="flex-shrink-0 w-full md:w-1/2">
          <motion.img
            src="assets/logo_aboutus.jpg"
            alt="Gurukul ERP"
            className="rounded-2xl shadow-xl object-cover w-[500px] h-[350px]"
            whileHover={{ scale: 1.03 }}
          />
        </div>
        <div className="flex-grow text-left">
          <h3
            className="text-3xl font-semibold mb-5"
            style={{ color: COLORS.primaryDark }}
          >
            Why Choose Gurukul ERP?
          </h3>
          <p className="text-lg mb-4 leading-relaxed" style={{ color: COLORS.textSecondary }}>
            Gurukul ERP offers a comprehensive, intuitive solution tailored for modern educational
            institutions. From real-time attendance tracking to advanced reporting tools, every
            feature is designed to make school operations more efficient and transparent.
          </p>
          <p className="text-lg leading-relaxed" style={{ color: COLORS.textSecondary }}>
            Supported by our dedicated team, Gurukul ERP helps educators focus on what truly
            matters — nurturing student success.
          </p>
        </div>
      </motion.section>

      {/* 🚀 Why Choose Us */}
      <motion.section
        className="max-w-6xl mx-auto py-20 px-6 text-center"
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <h2
          className="text-4xl font-bold mb-6"
          style={{ color: COLORS.primaryDark }}
        >
          Why Partner With Us?
        </h2>
        <p
          className="text-lg max-w-4xl mx-auto leading-relaxed"
          style={{ color: COLORS.textSecondary }}
        >
          At <strong>Shri Satya Technologies</strong>, we specialize in designing modern digital
          solutions that blend creativity, functionality, and scalability. Your success is our goal,
          and every project reflects our dedication to excellence.
        </p>
      </motion.section>
      <section
        className="py-16 px-6"
        style={{ backgroundColor: COLORS.background }}
      >
        <motion.h2
          className="text-4xl font-bold text-center mb-10"
          style={{ color: COLORS.primaryDark }}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
        >
          Our Core Strengths
        </motion.h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 max-w-6xl mx-auto">
          {services.map((service, index) => (
            <motion.div
              key={index}
              className="rounded-2xl p-8 text-center shadow-lg border transition-all duration-300 hover:shadow-2xl hover:-translate-y-1"
              style={{
                backgroundColor: COLORS.white,
                borderColor: COLORS.medium,
              }}
              whileHover={{ scale: 1.05 }}
            >
              {service.icon}
              <h3
                className="text-2xl font-semibold mb-3"
                style={{ color: COLORS.textPrimary }}
              >
                {service.title}
              </h3>
              <p className="text-base leading-relaxed" style={{ color: COLORS.textSecondary }}>
                {service.description}
              </p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* 💬 Connect Section */}
      <motion.section
        className="py-20 px-6 text-center"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        <h2
          className="text-4xl font-bold mb-4"
          style={{ color: COLORS.primaryDark }}
        >
          Connect With Us
        </h2>
        <p
          className="text-lg mb-6 max-w-3xl mx-auto leading-relaxed"
          style={{ color: COLORS.textSecondary }}
        >
          At <strong>Shri Satya Technologies</strong>, we bring together creativity and technology to
          craft exceptional ERP and web solutions. Let’s collaborate to build a smarter, more
          connected future for your institution.
        </p>
        <Link
          to="/contact"
          className="inline-block text-lg font-semibold py-3 px-8 rounded-full shadow-md transition duration-300"
          style={{
            backgroundColor: COLORS.primary,
            color: COLORS.white,
          }}
          onMouseOver={(e) => (e.target.style.backgroundColor = COLORS.primaryDark)}
          onMouseOut={(e) => (e.target.style.backgroundColor = COLORS.primary)}
        >
          Contact Us
        </Link>
      </motion.section>
    </div>
  );
};
export default AboutUs;
